import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteTwoTone, EditTwoTone, UploadOutlined } from "@ant-design/icons";
import {
    Button,
    Space,
    Upload,
    message,
    Row,
    Col,
    Typography,
    Dropdown,
    Menu,
    Popconfirm,
    Modal,
    Form,
    Input,
    Select,
    Switch,
} from "antd";
import ListTable from "../../../common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import { getData, postData, deleteData, putData } from "../../../apiServices/common";
import {
    UNIT_LIST_ENDPOINT,
    UNIT_ADD_ENDPOINT,
    UNIT_EDIT_ENDPOINT,
    UNIT_DELETE_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  UNIT_CREATE_PERMISSION,
  UNIT_EDIT_PERMISSION,
  UNIT_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const UnitConfig = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const [unitForm] = Form.useForm();
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllUnit = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${UNIT_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name: name }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setFetchError("Could not load this list.");
        message.error("Error getting unit data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllUnit(filterData);
  }, [refresh, filterData, getAllUnit]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    unitForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedUnit = async (id) => {
    setLoading(true);
    const response = await deleteData(`${UNIT_DELETE_ENDPOINT}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Unit Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const editSelectedUnit = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      status: formValues?.status,
      base_unit: formValues?.base_unit,
    };

    const response = await putData(
      `${UNIT_EDIT_ENDPOINT}/${formValues?.id}`,
      bodyData,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Unit Edited Successfully");
      refetch();
      unitForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewUnit = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      status: formValues?.status,
      base_unit: formValues?.base_unit,
    };

    const response = await postData(UNIT_ADD_ENDPOINT, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Warehouse Created Successfully");
      refetch();
      unitForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = (record) => {
    setIsEdit(true);
    unitForm.setFieldsValue({
      id: record?.id,
      name: record?.name,
      base_unit: record?.base_unit === "Yes" ? true : false,
    });
    setIsModalOpen(true);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Unit Name",
      dataIndex: ["name"],
      width: "40%",
      key: "unit_name",
    },
    {
      title: "Status",
      dataIndex: ["status"],
      width: "20%",
      key: "unit_status",
      render: (_, record) => {
        return <div>{record?.status === "1" ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Base Unit Status",
      dataIndex: ["base_unit"],
      width: "30%",
      key: "base_unit_status"
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedUnit(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([UNIT_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openModalForEdit(record);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([UNIT_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllUnit}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllUnit],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([UNIT_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([UNIT_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Unit Configuration</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllUnit(filterData)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Unit" : "Create Unit"}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <Form
          form={unitForm}
          layout="vertical"
          onFinish={isEdit ? editSelectedUnit : createNewUnit}
        >
          <Row gutter={6}>
            <Col span={14}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                placeholder="Unit Name"
                label="Unit Name"
                rules={[
                  {
                    required: true,
                    message: "Unit Name Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={7}>
              <Form.Item
                name="status"
                placeholder="Status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Status Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                name="base_unit"
                placeholder="Base Unit"
                label="Base Unit"
                rules={[
                  {
                    required: true,
                    message: "Base Unit Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
                loading={loading}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default UnitConfig;
